import type { ReactNode, InputHTMLAttributes, SelectHTMLAttributes, ButtonHTMLAttributes } from 'react'
import { useId } from 'react'

type Variant = 'filled' | 'tonal' | 'outlined' | 'text' | 'elevated'

export function Button({
  variant = 'filled',
  size,
  full,
  className = '',
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant
  size?: 'large'
  full?: boolean
}) {
  return (
    <button
      className={`btn btn-${variant} ${size === 'large' ? 'btn-large' : ''} ${full ? 'btn-full' : ''} ${className}`}
      {...rest}
    >
      {children}
    </button>
  )
}

export function TextField({
  label,
  help,
  error,
  className = '',
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: string; help?: string; error?: string }) {
  const id = useId()
  return (
    <div className={`field ${className}`}>
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="field-input"
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? `${id}-err` : help ? `${id}-help` : undefined}
        {...rest}
      />
      {error ? (
        <span className="field-error" id={`${id}-err`} role="alert">
          {error}
        </span>
      ) : help ? (
        <span className="field-help" id={`${id}-help`}>
          {help}
        </span>
      ) : null}
    </div>
  )
}

export function SelectField({
  label,
  help,
  children,
  className = '',
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { label: string; help?: string }) {
  const id = useId()
  return (
    <div className={`field ${className}`}>
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      <select id={id} className="field-input" {...rest}>
        {children}
      </select>
      {help ? (
        <span className="field-help" id={`${id}-help`}>
          {help}
        </span>
      ) : null}
    </div>
  )
}

export function Card({
  variant = 'elevated',
  className = '',
  children,
}: {
  variant?: 'elevated' | 'outlined' | 'filled'
  className?: string
  children: ReactNode
}) {
  return <div className={`card card-${variant} ${className}`}>{children}</div>
}

export function Chip({
  tone,
  selected,
  onClick,
  children,
}: {
  tone?: 'success' | 'warn' | 'error' | 'assist'
  selected?: boolean
  onClick?: () => void
  children: ReactNode
}) {
  const cls = `chip ${tone ? `chip-${tone}` : ''}`
  if (!onClick) return <span className={cls}>{children}</span>
  return (
    <button type="button" className={cls} aria-pressed={selected} onClick={onClick}>
      {children}
    </button>
  )
}

export function Switch({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  label: string
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      aria-disabled={disabled || undefined}
      className="switch"
      onClick={() => !disabled && onChange(!checked)}
    />
  )
}

export function Slider({
  value,
  min,
  max,
  step = 1,
  onChange,
  label,
}: {
  value: number
  min: number
  max: number
  step?: number
  onChange: (next: number) => void
  label: string
}) {
  const fill = ((value - min) / (max - min)) * 100
  return (
    <input
      type="range"
      className="slider"
      aria-label={label}
      value={value}
      min={min}
      max={max}
      step={step}
      style={{ ['--fill' as string]: `${fill}%` }}
      onChange={(e) => onChange(Number(e.target.value))}
    />
  )
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: { value: T; label: string }[]
  value: T
  onChange: (next: T) => void
  label: string
}) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

export function Banner({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'warn' | 'error' | 'success'
  children: ReactNode
}) {
  return (
    <div className={`banner banner-${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
      {children}
    </div>
  )
}

export function Stepper({ steps, current }: { steps: string[]; current: number }) {
  return (
    <div className="stepper" role="list" aria-label="Onboarding progress">
      {steps.map((label, i) => {
        const state = i < current ? 'complete' : i === current ? 'active' : 'upcoming'
        return (
          <div className="step" key={label} data-state={state} role="listitem">
            <span className="step-dot" aria-hidden="true">
              {state === 'complete' ? '✓' : i + 1}
            </span>
            <span className="step-label">{label}</span>
            {i < steps.length - 1 ? <span className="step-line" aria-hidden="true" /> : null}
          </div>
        )
      })}
    </div>
  )
}

export function Progress({ value, label }: { value: number; label: string }) {
  return (
    <div
      className="progress"
      role="progressbar"
      aria-valuenow={Math.round(value)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <div className="progress-bar" style={{ width: `${Math.min(100, Math.max(0, value))}%` }} />
    </div>
  )
}

export function SelectableCard({
  selected,
  disabled,
  onClick,
  children,
}: {
  selected: boolean
  disabled?: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      className="selectable"
      aria-pressed={selected}
      aria-disabled={disabled || undefined}
      onClick={onClick}
    >
      {children}
    </button>
  )
}
